<template>
  <div class="platform-selector" :class="{ mini }">
    <div class="platform-selector-item" v-for="platform in platforms" :key="platform" :class="{ active: isActive(platform) }">
      <button class="platform-selector-trigger" type="button" @click.stop="updateActive(platform)">
        <svg-loader :src="`/assets/images/icon-${platform}.svg`"></svg-loader>

        <div class="platform-selector-blink" v-if="showMobileBlink(platform) && !mini">
          <svg-loader class="platform-selector-alert" src="/assets/images/icon-doubts.svg"></svg-loader>
        </div>
      </button>

      <div class="platform-selector-actions" :class="{ active: showCopyFromDesktop(platform), rollback: showRollback(platform) }" v-if="desktopCopy">
        <button-el class="platform-selector-copy" type="button" @click="copyFromDesktop" :title="$t('builder.askDesktopCopy')">
          <svg-loader class="platform-selector-alert" src="/assets/images/icon-doubts.svg"></svg-loader>

          <svg-loader class="platform-selector-copy-icon" src="/assets/images/icon-copy-from-desktop.svg"></svg-loader>
          {{ $t('builder.desktopCopy') }}
        </button-el>

        <button-el class="platform-selector-rollback" type="button" @click="rollbackCheckout">
          <svg-loader src="/assets/images/icon-back-to-platform.svg"></svg-loader>
          {{ $t('builder.rollbackDesktopCopy') }}
        </button-el>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'

  export default {
    name: 'platform-selector',
    props: {
      mini: Boolean,
      active: String,
      desktopCopy: Boolean
    },
    data: () => ({
      platforms: ['desktop', 'mobile'],
      accessed: new Set(),
      copied: false,
      desktopUpdated: false
    }),
    computed: {
      ...mapGetters('builder', [
        'activeTemplate'
      ]),
      ...mapState('builder', [
        'lastUpdatedPlatform',
        'currentPlatform',
        'checkoutHistory',
        'lastInsertComponent'
      ]),
      isCurrentDesktop () {
        return this.active.indexOf('desktop') !== -1
      },
      desktopHasChanges () {
        return this.lastUpdatedPlatform !== null && this.lastUpdatedPlatform === 'desktop'
      }
    },
    watch: {
      active (active) {
        this.registerPlatformAccess(active)
      },
      lastUpdatedPlatform (lastPlatform) {
        if (lastPlatform === 'desktop') {
          this.copied = false
        }
      },
      lastInsertComponent (component) {
        if (component.position.template !== null && component.position.template.indexOf('mobile') !== -1) {
          this.copied = false
        }
      }
    },
    methods: {
      ...mapActions('builder', {
        copyMobileCheckoutToDesktop: 'COPY_MOBILE_CHECKOUT_TO_DESKTOP'
      }),
      ...mapMutations('builder', {
        setLastUpdatedPlatform: 'SET_LAST_UPDATED_PLATFORM',
        setTemplateUnsavedChanges: 'SET_TEMPLATE_UNSAVED_CHANGES',
        setMobileCheckout: 'SET_DESKTOP_CHECKOUT'
      }),
      isActive (platform) {
        return platform === this.active
      },
      isAccessed (platform) {
        return this.accessed.has(platform)
      },
      registerPlatformAccess (platform) {
        this.accessed.add(platform)
      },
      updateActive (platform) {
        this.registerPlatformAccess(platform)
        this.$event.emit('update-platform', platform)
        this.$emit('change', platform)
      },
      copyFromDesktop () {
        this.copyMobileCheckoutToDesktop()
        this.copied = true
        this.setLastUpdatedPlatform()
        this.setTemplateUnsavedChanges(true)

        this.$analytics('send', {
          hitType: 'event',
          eventCategory: 'Build Checkout',
          eventAction: 'copy-desktop'
        })
      },
      showCopyFromDesktop (platform) {
        return this.isActive(platform) && platform === 'mobile' && !this.copied && !this.showRollback(platform)
      },
      showMobileBlink (platform) {
        return this.desktopCopy && platform === 'mobile' && this.lastUpdatedPlatform === 'desktop' && this.currentPlatform === 'desktop' && !this.copied
      },
      showRollback (platform) {
        return this.isActive(platform) && platform === 'mobile' && this.copied
      },
      rollbackCheckout () {
        this.setMobileCheckout(this.checkoutHistory)
        this.copied = false

        this.$analytics('send', {
          hitType: 'event',
          eventCategory: 'Build Checkout',
          eventAction: 'undo-copy-desktop'
        })
      }
    },
    created () {
      this.registerPlatformAccess(this.active)
    },
    mounted () {
      this.$emit('change', this.active)
    }
  }
</script>

<style lang="scss">
  @keyframes blink-after {
    to {
      width: calc(100% + 14px);
      height: calc(100% + 14px);
      opacity: 0;
    }
  }

  @keyframes blink-before {
    to {
      width: calc(100% + 24px);
      height: calc(100% + 24px);
      opacity: 0;
    }
  }
</style>

<style lang="scss" scoped>
  @import '~stylesheets/meta.scss';

  $button-size: 45px;
  $button-mini-size: 36px;
  $button-svg-size: 20px;
  $button-mini-svg-size: 16px;

  .platform-selector {
    display: flex;
    justify-content: center;

    &.mini {
      .platform-selector-item {
        width: $button-mini-size;
        height: $button-mini-size;

        + .platform-selector-item {
          margin-left: $button-mini-svg-size;
        }

        svg {
          height: $button-mini-svg-size;
        }
      }

      .platform-selector-trigger {
        width: $button-mini-size;
        height: $button-mini-size;
      }
    }
  }

  .platform-selector-trigger {
    @include clear-button;
    width: $button-size;
    height: $button-size;
    background-color: #fff;
    border: 1px solid $color-pink;
    border-radius: 50%;

    &:before {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 2;
      border-radius: 50%;
      background-color: #fff;
      transition: .3s $transition-ease-out;
      transition-property: background-color;
      will-change: background-color;
      content: " "
    }

    .active &,
    &:hover {
      &:before {
        top: -1px;
        right: -1px;
        bottom: -1px;
        left: -1px;
        border: none;
        background-color: $color-pink;
      }

      > .svg-image path {
        fill: #fff;
      }
    }

    > .svg-image {
      position: relative;
      z-index: 3;

      svg {
        height: $button-svg-size;
      }

      path {
        fill: $color-pink;
        transition: .3s $transition-ease-out;
        transition-property: fill;
        will-change: fill;
      }
    }
  }

  .platform-selector-item {
    width: $button-size;
    height: $button-size;
    position: relative;

    + .platform-selector-item {
      margin-left: $button-svg-size * 2;
    }
  }

  .platform-selector-blink {
    &:after,
    &:before {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 1;
      border: 1px solid #ff9f00;
      border-radius: 50%;
      transform: translate(-50%, -50%);
      transition-property: width, opacity;
      will-change: width, opacity;
      content: " "
    }

    &:after {
      animation: 1s $transition-ease-in-out blink-after infinite;
    }

    &:before {
      animation: 1s $transition-ease-in-out blink-before infinite;
    }
  }

  .platform-selector-alert {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 3;
    width: 15px;
    height: 15px;
    background: #fff;
    border-radius: 50%;
    overflow: hidden;

    svg {
      path {
        fill: $color-blue;
      }
    }
  }

  .platform-selector-copy-icon {
    width: 26px;
  }

  .platform-selector-actions {
    position: absolute;
    top: 0;
    left: 50%;
    bottom: 0;
    height: 100%;
    width: 300px;
    overflow: hidden;
    pointer-events: none;

    &.active {
      pointer-events: auto;

      button {
        .platform-selector-alert {
          opacity: 1;
        }
      }

      .platform-selector-copy {
        transform: translate3d(0, 0, 0);
      }
    }

    &.rollback {
      pointer-events: auto;

      .platform-selector-copy {
        transform: translate3d(-110%, 0, 0);
      }

      .platform-selector-rollback {
        transform: translate3d(0, 0, 0);
      }
    }

    button {
      @include clear-button;
      height: 75%;
      margin: auto;
      margin-left: 10px;
      position: absolute;
      top: 0;
      bottom: 0;
      left: $button-size / 2;
      background: #fff;
      border: 1px solid $color-pink;
      border-radius: 25px;
      transform: translate3d(-110%, 0, 0);
      will-change: transform;
      color: $color-pink;
      font-size: 12px;
      text-transform: uppercase;

      ::v-deep .button-container {
        padding: 0 15px;
        display: flex;
        align-items: center;
      }

      &:hover {
        background: $color-pink;
        color: #fff;

        .svg-image:not(.platform-selector-alert) {
          svg {
            path {
              fill: #fff;
            }
          }
        }
      }

      .svg-image:not(.platform-selector-alert) {
        margin-right: 10px;

        svg {
          path {
            fill: $color-pink;
          }
        }
      }

      .platform-selector-alert {
        opacity: 0;
        top: -5px;
        right: -5px;
      }
    }
  }
</style>
